import React, { useMemo } from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from 'recharts';
import { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';
import { SurveyGroupTrendsLegendRows } from 'src/pages/survey-results/components/survey-group-trends-metric-legend';

/** Minimal event marker for overlay (same shape as MetricChart's ChartEventMarker). */
interface ChartEventMarker {
  id: string;
  event_date: string;
  short_title: string;
}

interface MetricStackedBarChartProps {
  className?: string;
  title?: string;
  data: TimeSeriesDataPoint[];
  metrics: Array<{
    key: string;
    name: string;
    color: string;
    imageUrl?: string;
  }>;
  isLoading: boolean;
  isNormalized?: boolean;
  sharedYAxisDomain?: boolean; // When true, all Y-axes use the same domain
  eventMarkers?: ChartEventMarker[];
  onEventMarkerClick?: (markerId: string) => void;
  showBrush?: boolean;
}

function getDateOnly(iso: string): string {
  return iso.split('T')[0];
}

/** Clickable label for event marker (SVG only for Recharts). */
function EventMarkerLabel({
  viewBox,
  marker,
  onEventMarkerClick,
}: {
  viewBox?: { x: number; y: number; width: number; height: number };
  marker: ChartEventMarker;
  onEventMarkerClick?: (id: string) => void;
}) {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  const handleClick = onEventMarkerClick ? () => onEventMarkerClick(marker.id) : undefined;
  const [hovered, setHovered] = React.useState(false);

  const tooltipText =
    marker.short_title.length > 50 ? `${marker.short_title.slice(0, 50)}…` : marker.short_title;
  const tooltipMinWidth = 100;
  const tooltipMaxWidth = 350;
  const tooltipCharApprox = 7;
  const tooltipPaddingX = 40;
  const tooltipWidth = Math.max(
    tooltipMinWidth,
    Math.min(tooltipMaxWidth, tooltipText.length * tooltipCharApprox + tooltipPaddingX)
  );
  // Clamp tooltip so it doesn't get cut off by the SVG top bounds.
  const tooltipAboveY = -28;
  const tooltipMinY = -y + 10;
  const tooltipY = Math.max(tooltipAboveY, tooltipMinY);

  return (
    <g
      className='bg-white !important'
      transform={`translate(${x},${y - 4})`}
      onClick={handleClick}
      style={{ cursor: handleClick ? 'pointer' : 'default' }}
      role={handleClick ? 'button' : undefined}
      fill='#fff'
    >
      <circle
        r={4}
        fill="#3257FB"
        stroke="#fff"
        strokeWidth={1}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      {/* Larger (almost invisible) hover target so native tooltip reliably appears */}
      <circle
        r={10}
        fill="#3257FB"
        opacity={0.01}
        stroke="transparent"
        pointerEvents="all"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
      {/* Reliable hover tooltip (SVG) */}
      <g
        transform={`translate(${-tooltipWidth / 2},${tooltipY})`}
        pointerEvents="none"
        opacity={hovered ? 1 : 0}
      >
        <rect
          x={0}
          y={0}
          width={tooltipWidth}
          height={24}
          rx={8}
          fill="#ffffff"
          stroke="#3257FB"
          strokeWidth={1}
          opacity={1}
        />
        <text
          x={tooltipWidth / 2}
          y={16}
          textAnchor="middle"
          fill="#1f2a37"
          fontSize={11}
          fontWeight={500}
        >
          {tooltipText}
        </text>
      </g>
    </g>
  );
}

const MetricStackedBarChart: React.FC<MetricStackedBarChartProps> = ({
  className,
  title,
  data,
  metrics,
  isLoading,
  isNormalized = false,
  sharedYAxisDomain = false,
  eventMarkers,
  onEventMarkerClick,
  showBrush = false,
}) => {
  // Normalize data for chart display while preserving original values
  const processedData = React.useMemo(() => {
    if (!isNormalized || data.length === 0 || metrics.length === 0) {
      return data.map(point => ({
        ...point,
        originalValues: { ...point }, // Store original values for tooltips
      }));
    }

    const enabledMetricKeys = metrics.map(metric => metric.key);

    // Find min and max values for each enabled metric across all data points
    const metricRanges: Record<string, { min: number; max: number }> = {};

    enabledMetricKeys.forEach(key => {
      const values = data
        .map(point => point[key] as number)
        .filter(val => typeof val === 'number' && !isNaN(val));

      if (values.length > 0) {
        metricRanges[key] = {
          min: Math.min(...values),
          max: Math.max(...values),
        };
      }
    });

    // Normalize the data points (0-100 scale)
    return data.map(point => {
      const normalizedPoint = { ...point, originalValues: { ...point } } as any;

      enabledMetricKeys.forEach(key => {
        const value = point[key] as number;
        const range = metricRanges[key];

        if (range && typeof value === 'number' && !isNaN(value)) {
          const rangeSpan = range.max - range.min;
          if (rangeSpan > 0) {
            // Normalize to 0-100 scale
            normalizedPoint[key] = ((value - range.min) / rangeSpan) * 100;
          } else {
            normalizedPoint[key] = 50; // If all values are the same, put them in the middle
          }
        }
      });

      return normalizedPoint;
    });
  }, [data, isNormalized, metrics]);

  const formatDateShort = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const formatDateFull = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate shared Y-axis domain if needed
  const sharedDomain = React.useMemo(() => {
    if (!sharedYAxisDomain || data.length === 0 || metrics.length === 0) {
      return undefined;
    }

    const allValues: number[] = [];
    metrics.forEach(metric => {
      data.forEach(point => {
        const value = point[metric.key] as number;
        if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
          allValues.push(value);
        }
      });
    });

    if (allValues.length === 0) {
      return undefined;
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // Round min down to nearest 5 and max up to nearest 5
    const roundedMin = Math.floor(min / 5) * 5;
    const roundedMax = Math.ceil(max / 5) * 5;

    return [Math.max(0, roundedMin), roundedMax];
  }, [sharedYAxisDomain, data, metrics]);

  const markersInRange = useMemo(() => {
    if (!eventMarkers?.length || !processedData.length) return [];
    const dates = new Set(processedData.map((d) => d.date));
    return eventMarkers.filter((m) => dates.has(getDateOnly(m.event_date)));
  }, [eventMarkers, processedData]);

  const hasLegendImages = metrics.some((m) => Boolean(m.imageUrl));

  const formatMetricValue = (value: number, metricKey: string): string => {
    if (!isFinite(value) || isNaN(value)) {
      return 'N/A';
    }
    if (metricKey === 'ctr' || metricKey === 'video_completion_rate' || metricKey === 'audio_completion_rate') {
      return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}%`;
    }
    if (metricKey === 'spend' || metricKey === 'cpa' || metricKey === 'cpm') {
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(1)}k`;
      }
      return `$${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  if (isLoading) {
    return (
      <div className={`rounded-2xl bg-white border border-gray-400 p-4 lg:p-8 flex-1 min-w-[200px] ${className}`}>
        {title && (
          <div className="text-2xl font-light mb-4">
            {title}
          </div>
        )}
        <Skeleton className="w-full h-[300px]" />
      </div>
    );
  }

  return (
    <div className={`rounded-2xl bg-white border border-gray-400 p-4 lg:p-8 flex-1 min-w-[200px] ${className}`}>
      {title && (
        <div className="text-2xl font-light mb-4">
          {title}
        </div>
      )}
      <div className={showBrush ? 'h-[260px]' : 'h-[200px]'}>
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <BarChart
            data={processedData}
            margin={{
              top: 10,
              right: 20,
              left: 0,
              bottom: showBrush ? 30 : 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateShort}
              minTickGap={30}
            />
            <YAxis
              yAxisId="left"
              domain={sharedDomain || undefined}
              tickFormatter={(value) =>
                isNormalized ? `${value.toFixed(0)}%` : formatMetricValue(value, metrics[0]?.key || '')
              }
            />
            <Tooltip
              labelFormatter={formatDateFull}
              formatter={(value: number, name: string, props: any) => {
                const metric = metrics.find(m => m.name === name);
                if (!metric) return [value, name];

                // If normalized, show original values in tooltip
                if (isNormalized && props.payload && props.payload.originalValues) {
                  const originalValue = props.payload.originalValues[metric.key];
                  if (typeof originalValue === 'number') {
                    return [formatMetricValue(originalValue, metric.key), metric.name];
                  }
                }

                return [formatMetricValue(value, metric.key), metric.name];
              }}
            />
            <Legend
              {...(hasLegendImages
                ? { content: () => <SurveyGroupTrendsLegendRows metrics={metrics} /> }
                : {})}
            />
            {metrics.map((metric) => (
              <Bar
                key={metric.key}
                dataKey={metric.key}
                name={metric.name}
                fill={metric.color}
                stackId="a"
                radius={[0, 0, 0, 0]}
                yAxisId="left"
              />
            ))}
            {markersInRange.map((marker) => (
              <ReferenceLine
                key={marker.id}
                x={getDateOnly(marker.event_date)}
                yAxisId="left"
                stroke="#3257FB"
                strokeDasharray="2 2"
                strokeOpacity={0.8}
                label={(
                  <EventMarkerLabel
                    marker={marker}
                    onEventMarkerClick={onEventMarkerClick}
                  />
                )}
              />
            ))}
            {showBrush && (
              <Brush
                dataKey="date"
                height={24}
                stroke="#3257FB"
                fill="#f1f5f9"
                tickFormatter={formatDateShort}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MetricStackedBarChart;

